/**
 * Turning a game's scores into a review.
 *
 * The worker scores every position of the game once, each from the point of
 * view of whoever is to move there. That is all the information needed: what a
 * move cost you is the difference between what the position was worth to you
 * before you played it and what it was worth to you after, and "after" is the
 * next position with its sign flipped. Hence `loss = score[i] + score[i + 1]`,
 * which looks like a typo and is not.
 *
 * Everything here is a pure function of those numbers, so it can be tested
 * without an engine, a worker or a browser -- and so the thresholds that decide
 * what counts as a mistake sit somewhere they can be read and argued with,
 * rather than inside the search.
 */
import { Color } from "@xq/rules";

export type Verdict = "best" | "good" | "inaccuracy" | "mistake" | "blunder";

export interface PlyReview {
  /** 0-based index into the game's move list */
  ply: number;
  side: Color;
  /** what was played */
  uci: string;
  /** what the engine would have played, when it differs */
  best: string | null;
  /** centipawns given away, never negative */
  loss: number;
  verdict: Verdict;
  /** the position's value after the move, from Red's side, for the graph */
  scoreAfter: number;
}

export interface SideReview {
  accuracy: number;
  /** average centipawns lost per move */
  avgLoss: number;
  moves: number;
  counts: Record<Verdict, number>;
}

export interface GameReview {
  plies: PlyReview[];
  red: SideReview;
  black: SideReview;
}

/**
 * A won position is won: past this, more is not better.
 *
 * Without a cap, one mating line makes every earlier move look like a rounding
 * error and the whole review collapses to "the last move was everything". The
 * engine's mate scores are near a million, so this matters rather a lot.
 */
const CAP = 1500;
const clamp = (v: number) => Math.max(-CAP, Math.min(CAP, v));

/**
 * Where the lines between the verdicts fall, in centipawns given away.
 *
 * Judgement, not arithmetic: a xiangqi horse is worth about 400, so a mistake
 * starts well below the price of a piece, and a blunder is roughly "you have
 * given away a horse or worse". `best` is reserved for actually finding the
 * engine's move rather than merely coming close to its valuation.
 */
const GOOD = 50;
const INACCURACY = 120;
const MISTAKE = 300;

function verdictFor(loss: number, playedBest: boolean): Verdict {
  if (playedBest) return "best";
  if (loss <= GOOD) return "good";
  if (loss <= INACCURACY) return "inaccuracy";
  if (loss <= MISTAKE) return "mistake";
  return "blunder";
}

/**
 * Accuracy from average loss.
 *
 * Our own curve, not anybody else's: 0 lost is 100%, and it decays smoothly so
 * that giving away about a pawn a move lands near 60%. It exists to be
 * comparable with your own past games, and no claim is made that it lines up
 * with the number any other site would print for the same moves.
 */
export function accuracyFrom(avgLoss: number): number {
  return Math.round(Math.max(0, Math.min(100, 100 * Math.exp(-Math.max(0, avgLoss) / 200))));
}

const emptyCounts = (): Record<Verdict, number> =>
  ({ best: 0, good: 0, inaccuracy: 0, mistake: 0, blunder: 0 });

/**
 * @param scores  one per position INCLUDING the start, so `moves.length + 1`.
 *                Each is from the point of view of whoever moves there.
 * @param ucis    the moves actually played.
 * @param bests   the engine's choice at each position; only the first
 *                `moves.length` are used.
 * @param first   who moved first. Red, in every game this app produces.
 */
export function reviewGame(
  scores: number[],
  ucis: string[],
  bests: (string | null)[],
  first: Color = Color.Red,
): GameReview {
  const plies: PlyReview[] = [];
  for (let i = 0; i < ucis.length; i++) {
    // A position that was never scored (an interrupted review) ends the list
    // rather than being guessed at.
    if (i + 1 >= scores.length) break;
    const side = i % 2 === 0 ? first : first === Color.Red ? Color.Black : Color.Red;
    const before = clamp(scores[i]!);
    const after = clamp(scores[i + 1]!);
    // Both are "good for whoever moves there", so the mover's view of the
    // position they created is -after, and the cost is before - (-after).
    const loss = Math.max(0, Math.round(before + after));
    const best = bests[i] ?? null;
    const playedBest = !!best && best === ucis[i];
    plies.push({
      ply: i,
      side,
      uci: ucis[i]!,
      best: playedBest ? null : best,
      loss: playedBest ? 0 : loss,
      verdict: verdictFor(loss, playedBest),
      // The graph wants one consistent side, so everything is shown from Red's.
      scoreAfter: side === Color.Red ? -after : after,
    });
  }

  const side = (c: Color): SideReview => {
    const mine = plies.filter((p) => p.side === c);
    const counts = emptyCounts();
    for (const p of mine) counts[p.verdict] += 1;
    const avg = mine.length ? mine.reduce((a, p) => a + p.loss, 0) / mine.length : 0;
    return { accuracy: accuracyFrom(avg), avgLoss: Math.round(avg), moves: mine.length, counts };
  };

  return { plies, red: side(Color.Red), black: side(Color.Black) };
}
